import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import WSTextField, { TextType } from "../../Components/TextBox/WSTextBox";
import { callAPI, Method, handleApiError, updateMainHeader } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { Grid, Paper, Typography } from "@mui/material";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { HeaderList, IHeaderListActions } from "../../Components/Header/HeaderList";

// Interface for Vendor form data
interface IVendorForm {
  vendorName: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  gstNumber?: string;
  openingBalance?: number | string;
}

export const CreateVendor: React.FC<any> = React.memo(() => {
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const params = useParams();
  const id = params?.id;
  const isEditMode = !!id;

  const [loading, setLoading] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null); // Ref for Enter-key submission

  const { control, handleSubmit, register, setValue, reset } = useForm<IVendorForm>({
    defaultValues: {
      vendorName: "",
      contactPerson: "",
      email: "",
      phone: "",
      address: "",
      gstNumber: "",
      openingBalance: 0,
    },
  });

  // Header & Breadcrumbs
  useEffect(() => {
    const title = isEditMode ? "Edit Vendor" : "Create Vendor";
    updateMainHeader(title);
    breadCrumbsStore.updateBreadCrumbs([
      { Vendors: "/vendor/list" },
      { [title]: `` },
    ]);
  }, [isEditMode]);

  // Fetch Vendor Data for Edit Mode
  useEffect(() => {
    async function getVendorData(vendorId: string) {
      try {
        setLoading(true);
        const response: any = await callAPI("Vendor", Method.GET, null, vendorId);
        const fetchedVendorData = response?.data || response?.vendor || response;

        if (fetchedVendorData && !response.errors && typeof fetchedVendorData === "object") {
          reset({
            vendorName: fetchedVendorData.vendorName || "",
            contactPerson: fetchedVendorData.contactPerson || "",
            email: fetchedVendorData.email || "",
            phone: fetchedVendorData.phone || "",
            address: fetchedVendorData.address || "",
            gstNumber: fetchedVendorData.gstNumber || "",
            openingBalance:
              fetchedVendorData.openingBalance !== undefined && fetchedVendorData.openingBalance !== null
                ? Number(fetchedVendorData.openingBalance)
                : 0,
          });
        } else {
          showMessage("Vendor not found or data error.", "error");
        }
      } catch (error) {
        console.error("Error fetching Vendor data:", error);
        showMessage("Error fetching Vendor data.", "error");
      } finally {
        setLoading(false);
      }
    }

    if (id) getVendorData(id);
  }, [id, reset, showMessage]);

  // Handle Cancel
  const handleCancel = () => navigate("/vendor/list");

  // Handle form submission
  const onSubmit = async (data: IVendorForm) => {
    const method = isEditMode ? Method.PUT : Method.POST;
    const endpoint = "Vendor";
    const successMsg = isEditMode ? "Vendor updated successfully" : "Vendor added successfully";

    try {
      setLoading(true);
      const payload = { ...data, openingBalance: Number(data.openingBalance) || 0 };
      const response: any = await callAPI(endpoint, method, payload, isEditMode ? id : null);

      if (response?.errors) {
        showMessage(handleApiError(response), "error");
      } else {
        showMessage(successMsg, "success");
        navigate("/vendor/list");
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? "updating" : "adding"} vendor:`, error);
      showMessage(`Error ${isEditMode ? "updating" : "adding"} vendor`, "error");
    } finally {
      setLoading(false);
    }
  };

  // Enter-key submission
  useEffect(() => {
    const handleEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        formRef.current?.requestSubmit(); // Triggers form submission
      }
    };
    window.addEventListener("keydown", handleEnter);
    return () => window.removeEventListener("keydown", handleEnter);
  }, []);

  const actions: IHeaderListActions[] = [
    { type: "button", text: "Cancel", size: "small", onClick: handleCancel, variant: "outlined", color: "error" },
    { type: "button", text: "Save", loading, size: "small", buttonType: "submit", variant: "outlined", color: "primary" },
  ];

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <HeaderList
        title={isEditMode ? "Update Vendor" : "Add Vendor"}
        isBackButton
        url="/vendor/list"
        type="card"
        actions={actions}
        isDivider
        headerSx={{ height: 50 }}
      />

      <Paper elevation={3} style={{ padding: 20, marginTop: 0, border: "1px solid #ccc", borderRadius: "8px" }}>
        <Typography sx={{ fontSize: ".9rem", color: "gray", paddingBottom: "8px" }}>Vendor Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <WSTextField
              control={control}
              id="vendorName"
              {...register("vendorName")}
              name="vendorName"
              rules={{ required: "Vendor Name is required" }}
              label="Vendor Name *"
              type={TextType.Text}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <WSTextField control={control} id="contactPerson" {...register("contactPerson")} name="contactPerson" label="Contact Person" type={TextType.Text} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <WSTextField control={control} id="email" {...register("email")} name="email" label="Email" type={TextType.Email} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <WSTextField control={control} id="phone" {...register("phone")} name="phone" label="Phone" type={TextType.AllowOnlyNumber} />
          </Grid>
          <Grid item xs={12}>
            <WSTextField control={control} id="address" {...register("address")} name="address" label="Address" type={TextType.Text} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <WSTextField control={control} id="gstNumber" {...register("gstNumber")} name="gstNumber" label="GST Number" type={TextType.Text} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <WSTextField control={control} id="openingBalance" {...register("openingBalance")} name="openingBalance" label="Opening Balance" type={TextType.Number} />
          </Grid>
        </Grid>
      </Paper>
    </form>
  );
});
